import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { getTrainingScoreConfigApi, updateTrainingScoreConfigApi } from '@/lib/reports-api';
import type { TrainingScoreConfig } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #173 — 管理员「系统设置 → 训练合格分规则」。
 *
 * 真实 GET/PATCH /system/training-score-config（ADMIN+，按 orgId 隔离）。规则用于
 * 训练完成评分：默认满分 100、合格 90、每错一题扣 10、错够 2 题直接不及格。
 * 越界由服务端钳制；保存成功后影响后续训练评分。仅 ADMIN+ 可编辑。
 */
const INPUT =
  'w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

type Draft = Record<keyof TrainingScoreConfig, string>;

function toDraft(c: TrainingScoreConfig): Draft {
  return {
    baseScore: String(c.baseScore),
    passScore: String(c.passScore),
    penaltyPerWrong: String(c.penaltyPerWrong),
    failAtWrongCount: String(c.failAtWrongCount),
    perQuestionPassScore: String(c.perQuestionPassScore),
  };
}

export function TrainingScoreSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'training-score-config'],
    queryFn: getTrainingScoreConfigApi,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data?.config) setDraft(toDraft(query.data.config));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: Partial<TrainingScoreConfig>) => updateTrainingScoreConfigApi(body),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(toDraft(data.config));
      await queryClient.invalidateQueries({ queryKey: ['system', 'training-score-config'] });
    },
    onError: (err) =>
      setSaveError(err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。'),
  });

  if (query.isLoading) {
    return (
      <Card title="训练合格分规则">
        <LoadingState label="正在加载合格分规则…" />
      </Card>
    );
  }
  if (query.isError || !draft) {
    return (
      <Card title="训练合格分规则">
        <ErrorState
          title="加载合格分规则失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const set = (key: keyof Draft, value: string) => {
    setSaved(false);
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    mutation.mutate({
      baseScore: Number(draft.baseScore),
      passScore: Number(draft.passScore),
      penaltyPerWrong: Number(draft.penaltyPerWrong),
      failAtWrongCount: Number(draft.failAtWrongCount),
      perQuestionPassScore: Number(draft.perQuestionPassScore),
    });
  };

  return (
    <Card
      title="训练合格分规则"
      description="用于 SOP 训练完成评分：满分基准 − 每错一题扣分；错够设定题数直接不及格；最终得分达合格分才算通过。默认 90 分合格、错 1 题扣 10、错 2 题不及格。"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Row label="合格分" hint="默认 90">
          <input className={INPUT} type="number" value={draft.passScore} disabled={!canEdit} onChange={(e) => set('passScore', e.target.value)} data-testid="training-pass-score" />
        </Row>
        <Row label="每错一题扣分" hint="默认 10">
          <input className={INPUT} type="number" value={draft.penaltyPerWrong} disabled={!canEdit} onChange={(e) => set('penaltyPerWrong', e.target.value)} />
        </Row>
        <Row label="错几题直接不及格" hint="默认 2">
          <input className={INPUT} type="number" value={draft.failAtWrongCount} disabled={!canEdit} onChange={(e) => set('failAtWrongCount', e.target.value)} />
        </Row>
        <Row label="单题合格分（低于视为错题）" hint="默认 60">
          <input className={INPUT} type="number" value={draft.perQuestionPassScore} disabled={!canEdit} onChange={(e) => set('perQuestionPassScore', e.target.value)} />
        </Row>
        <Row label="满分基准" hint="默认 100">
          <input className={INPUT} type="number" value={draft.baseScore} disabled={!canEdit} onChange={(e) => set('baseScore', e.target.value)} />
        </Row>
      </div>

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button className={PRIMARY_BTN} onClick={onSave} disabled={mutation.isPending}>
            {mutation.isPending ? '保存中…' : '保存规则'}
          </button>
          {saved ? <span className="text-xs text-emerald-600">已保存，后续训练评分将使用新规则。</span> : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改合格分规则。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{saveError}</div>
      ) : null}
    </Card>
  );
}

function Row({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-600">
        {label}
        <span className="ml-1 text-xs text-slate-400">（{hint}）</span>
      </span>
      {children}
    </label>
  );
}
